import { ApolloClient, ApolloLink, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { shouldSuppressSnackbar } from '@openx/utils/apollo/shouldSuppressSnackbar.ts';
import type { Auth } from 'firebase/auth';
import { fetchAuthToken } from '../../firebaseIntegration/utils.ts';
import { getRollbar } from '../../modules/analytics/rollbar.ts';
import { cache } from './cache.ts';
import { runLocalHandlers } from './runLocalHandlers.ts';
import type { MakeApolloClientArgs } from './types.ts';

const createAuthLink = (auth: Auth) =>
  setContext(async () => {
    const token = await fetchAuthToken({ auth });

    return {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

const httpLink = new HttpLink({
  uri: `${window.location.origin}/api/graphql`,
});

export const makeApolloClient = ({ handlersConfigMap, printError, auth }: MakeApolloClientArgs) => {
  const errorLink = onError(({ graphQLErrors, operation, response, networkError }) => {
    if (operation.getContext().disableGlobalNotification) {
      return;
    }

    if ((!graphQLErrors && !networkError) || shouldSuppressSnackbar(operation, response)) return;
    const rollbar = getRollbar();
    const handlersResults = runLocalHandlers({ graphQLErrors: graphQLErrors ?? [], handlersConfigMap, operation });
    const handledLocally = handlersResults.some(Boolean);

    if (!handledLocally && graphQLErrors)
      graphQLErrors.forEach(({ message, path }) => {
        printError(`[GraphQL error]: Message: ${message}${path ? `, Path: ${path}` : ''}`);
      });
    if (!handledLocally && networkError) printError(`[Network error]: ${networkError}`);

    if (rollbar) {
      rollbar.error('Caught by Apollo OnError interceptor', {
        caughtErrors: graphQLErrors,
        handledLocally,
      });
    }
  });

  const authLink = createAuthLink(auth);

  return new ApolloClient({
    cache,
    link: ApolloLink.from([errorLink, authLink, httpLink]),
  });
};
